import { ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DigitalTwinSettingDialog } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialog'
import { getSettingTarget } from 'ui/components/DigitalTwinSettingEditor/editorSetting.helper'
import {
  getEditorTitle,
  getPropertyHelpText,
  getPropertyTitle,
  getSubmitLabel,
} from 'ui/components/DigitalTwinSettingEditor/editorTitle.helper'
import { SettingEditorBanners } from 'ui/components/DigitalTwinSettingEditor/SettingEditorBanners'
import { getSettingEditorState } from 'ui/components/DigitalTwinSettingEditor/settingEditorState.helper'
import { useConfirmedClose } from 'ui/components/DigitalTwinSettingEditor/useConfirmedClose'
import { useTimeSeriesSettingAccess } from 'ui/components/DigitalTwinSettingForm/useTimeSeriesSettingAccess'
import { PropertySettings } from './PropertySettings'
import { SplitViewLayout } from './SplitViewLayout'
import type { PaneProps, SplitViewPane } from './splitViewPane.types'

export function SettingFormPane({
  form,
  editor,
  titleId,
  onClose,
  headerActions,
  ...context
}: PaneProps & { form: Extract<SplitViewPane, { pane: 'form' }> }): ReactNode {
  const { t } = useTranslation()
  const { requestClose, onUnsavedChangesChange, closeConfirmDialog } = useConfirmedClose(onClose)
  const hasAccessToTimeseries = useTimeSeriesSettingAccess()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { digitalTwin, isCollectedSettings } = context

  const { setting } = form
  const { mode, isTimeSeries, isEventSetting, isBaseSettingInCollectedSettings, isLocked } = getSettingEditorState({
    setting,
    copy: form.copy,
    isCollectedSettings,
  })
  const lacksTimeSeriesAccess = isTimeSeries && !hasAccessToTimeseries

  const submitLabel = getSubmitLabel({ isTimeSeries, mode }, t)

  const handleSettingModified = (): void => {
    editor.markModified()
    context.onSettingModified()
  }

  return (
    <DigitalTwinSettingDialog
      initData={setting}
      configurableProperties={context.configurableProperties}
      propertyItems={context.propertyItems}
      digitalTwin={digitalTwin}
      allowEmpty={false}
      priorities={context.priorities}
      baseSettingPriorities={context.baseSettingPriorities}
      isBaseSettings={context.isBaseSettings}
      isCollectedSettings={isCollectedSettings}
      hasUnitAndProperty={!form.choosesProperty}
      submitLabel={submitLabel}
      hideActions={lacksTimeSeriesAccess}
      disable={isLocked}
      onClose={onClose}
      onSettingModified={handleSettingModified}
      onSaveSuccess={editor.markSaved}
      onCopyChange={editor.setCopy}
      onUnsavedChangesChange={onUnsavedChangesChange}
      onRequestClose={requestClose}
      scrollContainerRef={scrollContainerRef}
      renderLayout={({ content, actions, newSetting }) => {
        const property = getSettingTarget(newSetting)
        return (
          <>
            <SplitViewLayout
              title={getEditorTitle({
                propertyTitle: property && getPropertyTitle(property, digitalTwin),
                editedSetting: { isTimeSeries, mode },
                t,
              })}
              helpText={property ? getPropertyHelpText(property, digitalTwin) : undefined}
              titleId={titleId}
              onClose={requestClose}
              headerActions={headerActions}
              scrollContainerRef={scrollContainerRef}
              footer={actions}
            >
              <div className="px-6 pt-4 empty:hidden">
                <SettingEditorBanners
                  lacksTimeSeriesAccess={lacksTimeSeriesAccess}
                  isEventSetting={isEventSetting}
                  mode={mode}
                  copyActionLabel={submitLabel}
                  isBaseSettingInCollectedSettings={isBaseSettingInCollectedSettings}
                />
              </div>
              {content}
              {property && (
                <PropertySettings
                  {...context}
                  key={`${property.level}.${property.name}.${property.attribute}`}
                  property={property}
                  revision={editor.historyRevision}
                  onEditSetting={editor.editSetting}
                />
              )}
            </SplitViewLayout>
            {closeConfirmDialog}
          </>
        )
      }}
    />
  )
}
